import React from "react";

import { Icon, IconName } from "@blueprintjs/core";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { SvgIcon } from "@/components/helpers/ui/SvgIcon";
import {
  ProgramComponentResponseQuery,
  ScenarioAssignmentDisplayStatus,
  ScenarioDifficulty,
  VisibilityScope,
} from "@/graphql";
import {
  AnalyticsScenario,
  FullScenario,
  ProgramScenario,
  RoleplayScenario,
} from "@/types/roleplays";
import { NonNullableArrayItem } from "@/utils/types";

export type ComponentResponseScenario = NonNullableArrayItem<
  NonNullable<
    ProgramComponentResponseQuery["programComponentResponse"]
  >["roleplayScenarios"]
>;

export function buildRoleplayUrl(roleplaySlug: string) {
  return `/roleplays/${roleplaySlug}`;
}

export function buildScenarioUrl({
  roleplaySlug,
  scenarioSlug,
}: {
  roleplaySlug?: string;
  scenarioSlug: string;
}) {
  if (!roleplaySlug) {
    return `/scenarios/${scenarioSlug}`;
  }
  return `/roleplays/${roleplaySlug}/${scenarioSlug}`;
}

export function buildSessionUrl({
  roleplaySlug,
  scenarioSlug,
  sessionUuid,
}: {
  scenarioSlug?: string;
  roleplaySlug?: string;
  sessionUuid?: string;
}) {
  if (!roleplaySlug) {
    return `/scenarios/${scenarioSlug}/${sessionUuid}`;
  }
  return `/roleplays/${roleplaySlug}/${scenarioSlug}/${sessionUuid}`;
}

export function buildCreateScenarioUrl() {
  return `/chat/new`;
}

export function buildIntakeUrl(intakeUuid: string) {
  return `/roleplays/intake/${intakeUuid}`;
}

export const CustomScenarioSegmentTitle = "Custom Scenarios";
export const CoreScenarioSegmentTitle = "Core Scenarios";

export const SCENARIO_SCOPE_ORDER = [
  {
    name: CustomScenarioSegmentTitle,
    scopes: [VisibilityScope.Workspace, VisibilityScope.User],
  },
  {
    name: CoreScenarioSegmentTitle,
    scopes: [VisibilityScope.Global],
  },
];

export const SCENARIO_DIFFICULTY_ORDER = {
  [ScenarioDifficulty.Custom]: 0,
  [ScenarioDifficulty.Easy]: 1,
  [ScenarioDifficulty.Medium]: 2,
  [ScenarioDifficulty.Hard]: 3,
  [ScenarioDifficulty.VeryHard]: 4,
};

export function sortScenariosByDifficulty<
  T extends
    | FullScenario
    | ComponentResponseScenario
    | AnalyticsScenario
    | ProgramScenario
    | RoleplayScenario,
>(scenarios: T[]): T[] {
  return [...scenarios].sort((a, b) => {
    const indexA = SCENARIO_DIFFICULTY_ORDER?.[a.difficulty] ?? 0;
    const indexB = SCENARIO_DIFFICULTY_ORDER?.[b.difficulty] ?? 0;
    return indexA - indexB;
  });
}

export const SCENARIO_ASSIGNMENT_STATUS_STYLES: Record<
  ScenarioAssignmentDisplayStatus,
  {
    textColor?: string;
    color: string;
    icon: React.ReactNode;
    iconClass?: IconName;
    text: string;
    borderColor: string;
    backgroundColor?: string;
  }
> = {
  [ScenarioAssignmentDisplayStatus.Completed]: {
    textColor: "#129B4C",
    color: "#129B4C",
    icon: <Icon icon="tick-circle" size={14} color={"#0D8050"} />,
    iconClass: "tick-circle",
    text: "Completed",
    borderColor: "#129B4C",
    backgroundColor: "#E6F5EF",
  },
  [ScenarioAssignmentDisplayStatus.PastDue]: {
    textColor: "#C18300",
    color: "#C18300",
    icon: <Icon icon="warning-sign" size={14} color={"#C18300"} />,
    iconClass: "warning-sign",
    text: "Past Due",
    borderColor: "#C18300",
    backgroundColor: "#FFFAF0",
  },
  [ScenarioAssignmentDisplayStatus.NotStarted]: {
    textColor: "#778397",
    color: "#F5F6F8",
    icon: <Icon icon="circle" size={14} color={"#778397"} />,
    iconClass: "circle",
    text: "Not Started",
    borderColor: "#b0b6bf",
    backgroundColor: "#F4F6FC",
  },
  [ScenarioAssignmentDisplayStatus.InProgress]: {
    textColor: "#215DB0",
    color: "#215DB0",
    icon: <EmbedSvg width={14} src={SvgIcon({ name: "on-going" })} />,
    iconClass: "circle",
    text: "In Progress",
    borderColor: "#BF7326",
    backgroundColor: "#E9F1FB",
  },
  [ScenarioAssignmentDisplayStatus.DidNotPass]: {
    textColor: "#D13438",
    color: "#D13438",
    icon: <Icon icon="cross-circle" size={14} color={"#C61D1D"} />,
    iconClass: "cross",
    text: "Did Not Pass",
    borderColor: "#C61D1D",
    backgroundColor: "#F5E6E8",
  },
};
